/**
 * The server's step-up summary as the code dialog's label/value rows
 * (StepUpDialog.jsx), kept free of React so node --test covers it. Nothing is
 * re-derived: each value is the server's, as text. An instant the server
 * sends (RFC 3339 with a time, such as a fee proposal's proposedAt) is shown
 * in Colombo time, as everywhere else in the Console (format.js); a date
 * (YYYY-MM-DD) and every other string are shown as they are. The keys the
 * dialog's note says in words (notes.js NOTED_KEYS, such as a statement
 * accept's downstream counts, IP-96) are left to the note, and an upload
 * batch's files to their own table (summaryFiles). Run day end (DAY_END_RUN,
 * IP-195, IP-200) shows its day, stage (in words) and reason as rows; its
 * counts are the note's (notes.js), its statements the warning's, and its
 * transactions their own table (summaryTransactions).
 */
import { STAGES } from '@lfoc/domain/business-day/stages.js';

import { dayShort } from '../businessDay/businessDay.js';
import { ORDER_TYPE_LABEL } from '../containers/transactions/approval/orders.js';
import { classLabelOf } from '../containers/uploads/uploadKinds.js';
import { formatInstant } from '../format.js';
import { NOTED_KEYS } from './notes.js';

/** An RFC 3339 instant: a date, a time, and Z or an offset - never a bare date. */
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

const labelOf = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

function textOf(value) {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' && INSTANT.test(value) && !Number.isNaN(Date.parse(value))) return formatInstant(value) + ' (Colombo time)';
    return String(value);
}

/** A statement's collection account, in a line: its fund, bank and number. */
const accountLine = (account) => (account ? [account.fundId, account.bankName, account.accountNumber].filter(Boolean).join(' ') : null);

/**
 * INTAKE_UPLOAD_BATCH: the files the one code uploads, as the dialog's table
 * shows them - File Name, File Category (its class and kind in words, and a
 * statement's account) and Effective Date - in the server's order; null for
 * any other summary.
 *
 * @returns {Array<{ key: string, fileName: string, category: string, account: string|null, effectiveDate: string }>|null}
 */
export function summaryFiles(summary) {
    if (!Array.isArray(summary?.files)) return null;
    return summary.files.map((file, i) => ({
        key: String(i),
        fileName: file?.fileName ?? '—',
        category: classLabelOf(file),
        account: accountLine(file?.account),
        effectiveDate: file?.businessDate ?? '—',
    }));
}

/** Run day end's keys said elsewhere in the dialog: in the note, the warning or the transactions' table. */
const DAY_END_RUN_ELSEWHERE = Object.freeze(['resume', 'statements', 'waitingForApproval', 'carriedOver', 'moved', 'movedTo', 'transactions',
    'moreTransactions']);

const stageText = (stage) => STAGES.find((s) => s.stage === stage)?.label ?? stage;

/** @returns {Array<{ key: string, label: string, children: string }>} every key but the action's own, the noted ones and a batch's files (summaryFiles) */
export function summaryRows(summary) {
    const dayEnd = summary?.action === 'DAY_END_RUN';
    return Object.entries(summary ?? {})
        .filter(([key, value]) => key !== 'action' && !NOTED_KEYS.includes(key) && !(key === 'files' && Array.isArray(value))
            && !(dayEnd && DAY_END_RUN_ELSEWHERE.includes(key)))
        .map(([key, value]) => ({ key, label: labelOf(key), children: dayEnd && key === 'stage' ? stageText(value) : textOf(value) }));
}

/**
 * DAY_END_RUN: the day's transactions waiting for approval as the dialog's table shows them -
 * Transaction, Fund, Type (in words) and What happens ("Carried over", or "Moves to 14 Oct") -
 * in the server's order (carried over first); null for any other summary.
 *
 * @returns {Array<{ key: string, transactionRef: string, fundId: string, type: string, outcome: string, moved: boolean }>|null}
 */
export function summaryTransactions(summary) {
    if (summary?.action !== 'DAY_END_RUN' || !Array.isArray(summary.transactions)) return null;
    return summary.transactions.map((t, i) => ({
        key: String(i),
        transactionRef: t.transactionRef ?? '—',
        fundId: t.fundId ?? '—',
        type: ORDER_TYPE_LABEL[t.orderType] ?? t.orderType ?? '—',
        outcome: t.outcome === 'MOVED' ? 'Moves to ' + dayShort(summary.movedTo) : 'Carried over',
        moved: t.outcome === 'MOVED',
    }));
}
